import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, UserCog, Pencil, DollarSign, Phone } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DeleteButton } from "@/components/DeleteButton";

const EMPTY = { name: "", email: "", phone: "", password: "" };

export default function StaffManagement() {
  const { user } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [convert, setConvert] = useState({ email: "", role: "worker" });
  const [converting, setConverting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [myPhone, setMyPhone] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setEdit = (k) => (e) => setEditing((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => { load(); }, []);
  useEffect(() => { if (user?.phone) setMyPhone(user.phone); }, [user]);
  async function load() { try { const { data } = await api.get("/staff/workers"); setWorkers(data); } catch { /* */ } }

  async function saveMyPhone(e) {
    e.preventDefault();
    setSavingPhone(true);
    try {
      await api.patch("/auth/my-phone", { phone: myPhone.trim() });
      toast.success("Your phone number was saved. You can now log in with a texted code.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setSavingPhone(false); }
  }

  async function submit(e) {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters.");
    setLoading(true);
    try {
      await api.post("/staff/workers", form);
      toast.success("Worker account created.");
      setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  function openEdit(w) {
    setEditing({ id: w.id, name: w.name || "", email: w.email || "", phone: w.phone || "", hourly_rate: w.hourly_rate ?? 0, password: "", emails: w.emails || [] });
  }

  async function saveEdit(e) {
    e.preventDefault();
    if (!editing.name.trim()) return toast.error("Name is required.");
    if (!editing.email.trim()) return toast.error("Email is required.");
    if (editing.password && editing.password.length < 6) return toast.error("Password must be at least 6 characters.");
    setSavingEdit(true);
    try {
      await api.patch(`/staff/workers/${editing.id}/profile`, {
        name: editing.name.trim(),
        email: editing.email.trim(),
        phone: editing.phone || "",
        hourly_rate: Number(editing.hourly_rate || 0),
        password: editing.password ? editing.password : null,
      });
      toast.success("Worker profile updated.");
      setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setSavingEdit(false); }
  }

  async function convertRole(e) {
    e.preventDefault();
    if (!convert.email) return toast.error("Enter the account email.");
    setConverting(true);
    try {
      const { data } = await api.patch("/staff/user-role", { email: convert.email.trim(), role: convert.role });
      toast.success(`${data.name || data.email} is now a ${data.role}.`);
      setConvert({ email: "", role: "worker" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setConverting(false); }
  }

  async function del(id) {
    try { await api.delete(`/staff/workers/${id}`); toast.success("Worker removed."); load(); }
    catch { toast.error("Failed."); }
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><Users className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Staff</h1>
          <p className="text-sm text-muted-foreground">Create worker accounts. Workers log in at the staff portal and see only Time Clock, Schedule and Time Off.</p>
        </div>
      </div>

      <form onSubmit={saveMyPhone} className="mt-6 grid gap-3 rounded-xl border border-border/60 bg-secondary/30 p-5 sm:grid-cols-[1fr_auto] sm:items-end">
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5 text-xs"><Phone className="h-3.5 w-3.5 text-primary" /> Your phone number (for texted login codes)</Label>
          <Input data-testid="my-phone" placeholder="(505) 555-0199" value={myPhone} onChange={(e) => setMyPhone(e.target.value)} />
        </div>
        <Button type="submit" disabled={savingPhone} variant="outline" className="rounded-full" data-testid="my-phone-save">Save my number</Button>
      </form>

      <form onSubmit={submit} className="mt-6 grid gap-3 rounded-xl border border-border/60 bg-card p-5 sm:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-1.5"><Label className="text-xs">Full name</Label><Input data-testid="worker-name" value={form.name} onChange={set("name")} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" data-testid="worker-email" value={form.email} onChange={set("email")} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Phone (optional)</Label><Input data-testid="worker-phone" value={form.phone} onChange={set("phone")} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Temp password</Label><Input type="text" data-testid="worker-password" value={form.password} onChange={set("password")} required /></div>
        <div className="flex items-end"><Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="worker-submit"><Plus className="mr-1 h-4 w-4" /> Add Worker</Button></div>
      </form>

      <form onSubmit={convertRole} className="mt-4 grid gap-3 rounded-xl border border-border/60 bg-secondary/30 p-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="lg:col-span-4 flex items-center gap-2 text-sm text-muted-foreground">
          <UserCog className="h-4 w-4 text-primary" /> Convert an existing staff account between admin and worker (e.g. so an admin can be assigned a schedule).
        </div>
        <div className="space-y-1.5 lg:col-span-2"><Label className="text-xs">Account email</Label><Input type="email" data-testid="convert-email" placeholder="person@example.com" value={convert.email} onChange={(e) => setConvert({ ...convert, email: e.target.value })} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">New role</Label>
          <Select value={convert.role} onValueChange={(v) => setConvert({ ...convert, role: v })}>
            <SelectTrigger data-testid="convert-role"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="worker">Worker</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="flex items-end"><Button type="submit" disabled={converting} variant="outline" className="w-full rounded-full" data-testid="convert-submit"><UserCog className="mr-1 h-4 w-4" /> Convert Role</Button></div>
      </form>

      <div className="mt-6 overflow-hidden rounded-xl border border-border/60 bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {workers.length === 0 ? <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No workers yet. Add your first worker above.</TableCell></TableRow>
              : workers.map((w) => (
                <TableRow key={w.id} data-testid={`worker-row-${w.id}`}>
                  <TableCell className="font-medium">{w.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {w.email}
                    {w.emails && w.emails.filter((e) => e !== w.email).length > 0 && (
                      <span className="block text-xs text-muted-foreground/70">also: {w.emails.filter((e) => e !== w.email).join(", ")}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{w.phone || "—"}</TableCell>
                  <TableCell className="text-right text-sm">${Number(w.hourly_rate || 0).toFixed(2)}/hr</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm" className="rounded-full" onClick={() => openEdit(w)} data-testid={`worker-edit-${w.id}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                      <DeleteButton onConfirm={() => del(w.id)} label={`worker ${w.name}`} testId={`worker-delete-${w.id}`} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="worker-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit worker profile</DialogTitle>
            <DialogDescription>Update this worker's details. Changing the email keeps the old one on file — they can log in with either.</DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="grid gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Full name</Label><Input data-testid="edit-worker-name" value={editing.name} onChange={setEdit("name")} required /></div>
              <div className="space-y-1.5"><Label className="text-xs">Email (login)</Label><Input type="email" data-testid="edit-worker-email" value={editing.email} onChange={setEdit("email")} required /></div>
              {editing.emails && editing.emails.length > 1 && (
                <p className="-mt-1 text-xs text-muted-foreground">Can log in with: {editing.emails.join(", ")}</p>
              )}
              <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input data-testid="edit-worker-phone" value={editing.phone} onChange={setEdit("phone")} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Hourly rate ($)</Label>
                <div className="relative"><DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input type="number" step="0.01" min="0" className="pl-8" data-testid="edit-worker-rate" value={editing.hourly_rate} onChange={setEdit("hourly_rate")} /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Reset password (optional)</Label><Input type="text" placeholder="Leave blank to keep current" data-testid="edit-worker-password" value={editing.password} onChange={setEdit("password")} /></div>
              <DialogFooter className="mt-2">
                <Button type="button" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" disabled={savingEdit} className="rounded-full" data-testid="edit-worker-save">Save changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
